import React, { useEffect, useState } from "react";
import PortalShell from "@/components/PortalShell";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { FileText, ClipboardCheck, Upload, Star, CheckCircle2, Clock } from "lucide-react";

function ProgressRow({ label, done }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className={`w-5 h-5 rounded-full grid place-items-center ${done ? "bg-emerald-500 text-white" : "border border-slate-300 text-slate-300"}`}>
        <CheckCircle2 className="w-3.5 h-3.5"/>
      </div>
      <div className={done ? "text-slate-400 line-through" : "text-slate-700"}>{label}</div>
    </div>
  );
}

export default function FamilyPortal() {
  const [state, setState] = useState(null);
  const [tab, setTab] = useState("intake");
  const [intake, setIntake] = useState({
    individual_name: "", dob: "", diagnosis: "", medicaid_id: "",
    address: "", emergency_contact_name: "", emergency_contact_phone: "",
    support_coordinator: "", notes: "",
  });
  const [evaluation, setEvaluation] = useState({
    period: "", overall_rating: 5, communication: 5, responsiveness: 5, care_quality: 5, comments: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/family/dashboard");
      setState(data);
      if (data.intake) {
        const cleaned = Object.fromEntries(Object.entries(data.intake).map(([k, v]) => [k, v ?? ""]));
        setIntake((prev) => ({ ...prev, ...cleaned }));
      }
    } catch (e) { toast.error(formatApiError(e)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };
  useEffect(() => { load(); }, []);

  const submitIntake = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/family/intake", intake);
      toast.success("Intake saved. Thank you!");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitEvaluation = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/family/evaluation", evaluation);
      toast.success("Performance evaluation submitted. Thank you!");
      setEvaluation({ period: "", overall_rating: 5, communication: 5, responsiveness: 5, care_quality: 5, comments: "" });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!state) {
    return <PortalShell variant="family"><div className="text-slate-500 text-sm" data-testid="family-loading">Loading your portal…</div></PortalShell>;
  }

  const intakeDone = Boolean(state.intake);
  const evalsDone = state.evaluations_count > 0;

  return (
    <PortalShell variant="family">
      <Toaster richColors position="top-right" />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <span className="wc-eyebrow-navy">Family Portal</span>
          <h1 className="mt-3 font-outfit text-4xl md:text-5xl font-semibold tracking-tight text-brand-navy">
            Welcome, {state.user.name.split(" ")[0]}.
          </h1>
          <p className="mt-3 text-slate-600 text-sm max-w-lg">Complete your intake, submit performance evaluations, and stay connected with our care team.</p>
        </div>
        <div className="wc-card p-6 min-w-[240px]" data-testid="family-progress-card">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your progress</div>
          <div className="mt-4 space-y-2.5">
            <ProgressRow label="New Client Intake" done={intakeDone}/>
            <ProgressRow label={`Performance Evaluations (${state.evaluations_count})`} done={evalsDone}/>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-10 flex gap-2 border-b border-slate-100">
        {[
          { id: "intake", label: "New Client Intake", icon: FileText },
          { id: "evaluation", label: "Performance Evaluation", icon: ClipboardCheck },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`family-tab-${id}`}
            className={`px-4 py-3 text-sm font-semibold inline-flex items-center gap-2 border-b-2 ${tab === id ? "border-brand-orange text-brand-navy" : "border-transparent text-slate-500 hover:text-brand-navy"}`}
            style={{ transition: "color 200ms ease, border-color 200ms ease" }}>
            <Icon className="w-4 h-4"/> {label}
          </button>
        ))}
      </div>

      {/* Intake */}
      {tab === "intake" && (
        <form onSubmit={submitIntake} className="mt-8 wc-card p-6 md:p-10 space-y-5" data-testid="intake-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { k: "individual_name", l: "Individual's name", req: true },
              { k: "dob", l: "Date of birth", type: "date" },
              { k: "diagnosis", l: "Primary diagnosis" },
              { k: "medicaid_id", l: "Medicaid ID" },
              { k: "address", l: "Home address" },
              { k: "support_coordinator", l: "Support Coordinator" },
              { k: "emergency_contact_name", l: "Emergency contact — name" },
              { k: "emergency_contact_phone", l: "Emergency contact — phone" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{f.l}</label>
                <input type={f.type || "text"} required={f.req} data-testid={`intake-${f.k}`}
                  value={intake[f.k]} onChange={(e)=>setIntake({...intake, [f.k]: e.target.value})}
                  className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"/>
              </div>
            ))}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes for the intake team</label>
            <textarea rows={4} data-testid="intake-notes" value={intake.notes} onChange={(e)=>setIntake({...intake, notes: e.target.value})}
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"/>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-xs text-slate-500 inline-flex items-center gap-2"><Upload className="w-4 h-4"/> Document uploads coming soon.</div>
            <button type="submit" disabled={busy} className="wc-btn-primary disabled:opacity-60" data-testid="intake-submit-btn">
              {busy ? "Saving…" : intakeDone ? "Update intake" : "Submit intake"}
            </button>
          </div>
        </form>
      )}

      {/* Evaluation */}
      {tab === "evaluation" && (
        <form onSubmit={submitEvaluation} className="mt-8 wc-card p-6 md:p-10 space-y-5" data-testid="evaluation-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Evaluation period</label>
            <input required placeholder="e.g. Q3 2026" data-testid="eval-period" value={evaluation.period} onChange={(e)=>setEvaluation({...evaluation, period: e.target.value})}
              className="mt-2 w-full max-w-xs rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"/>
          </div>
          {["overall_rating","communication","responsiveness","care_quality"].map((k) => (
            <div key={k}>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{k.replace(/_/g," ")}</label>
                <div className="text-brand-navy font-semibold text-sm" data-testid={`eval-value-${k}`}>{evaluation[k]}/5</div>
              </div>
              <div className="mt-3 flex gap-2">
                {[1,2,3,4,5].map((n) => (
                  <button type="button" key={n} onClick={() => setEvaluation({...evaluation, [k]: n})} data-testid={`eval-${k}-${n}`}
                    className={`w-10 h-10 rounded-xl border grid place-items-center ${evaluation[k] >= n ? "bg-brand-orange border-brand-orange text-white" : "border-slate-200 text-slate-400 hover:border-slate-300"}`}
                    style={{ transition: "background-color 150ms ease, color 150ms ease, border-color 150ms ease" }}>
                    <Star className="w-4 h-4" fill={evaluation[k] >= n ? "currentColor" : "none"}/>
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Comments</label>
            <textarea rows={4} data-testid="eval-comments" value={evaluation.comments} onChange={(e)=>setEvaluation({...evaluation, comments: e.target.value})}
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange"/>
          </div>
          <button type="submit" disabled={busy} className="wc-btn-primary disabled:opacity-60" data-testid="eval-submit-btn">
            {busy ? "Submitting…" : "Submit evaluation"}
          </button>
        </form>
      )}

      {/* Past evaluations */}
      {state.evaluations && state.evaluations.length > 0 && (
        <div className="mt-10 wc-card p-6 md:p-8" data-testid="past-evaluations">
          <h3 className="font-outfit text-lg font-semibold text-brand-navy">Your submitted evaluations</h3>
          <div className="mt-4 divide-y divide-slate-100">
            {state.evaluations.map((e) => (
              <div key={e.id} className="py-4 flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-brand-navy">{e.period}</div>
                  <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3"/> {new Date(e.created_at).toLocaleDateString()}</div>
                </div>
                <div className="text-brand-orange font-semibold text-sm">{e.overall_rating}/5</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </PortalShell>
  );
}
